import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FileCheck, RefreshCw, Clock, CheckCircle, X, Eye, Mail, Check,
  Calendar, Plus, Settings, Users, Upload, Printer, Download, FileText,
  AlertCircle, ChevronDown, ChevronUp, Trash2
} from 'lucide-react';
import { Button } from '../ui/button';
import { onboardingAPI } from '../../services/api';
import api from '../../services/api';
import { toast } from 'sonner';
import OnboardingFormsSettings from './OnboardingFormsSettings';
import UniversalUploader from '../common/UniversalUploader';
import DOMPurify from 'dompurify';

const OnboardingTab = ({
  onboardingTracker,
  fetchOnboardingTracker,
  setSelectedOnboardingWorker,
  setShowOnboardingModal
}) => {
  const [extendingWorker, setExtendingWorker] = useState(null);
  const [extendDays, setExtendDays] = useState(7);
  const [activeSubTab, setActiveSubTab] = useState('tracker');
  const [expandedWorker, setExpandedWorker] = useState(null);
  const [uploadingFor, setUploadingFor] = useState(null);
  const [processingTask, setProcessingTask] = useState(null);

  const handleExtendDeadline = async (workerId) => {
    try {
      await onboardingAPI.extendDeadline(workerId, extendDays, 'Extended by owner');
      toast.success(`Deadline extended by ${extendDays} days`);
      setExtendingWorker(null);
      fetchOnboardingTracker();
    } catch (e) {
      toast.error('Failed to extend deadline');
    }
  };

  // Mark task as complete by owner
  const handleMarkComplete = async (workerId, taskType, taskId) => {
    setProcessingTask(`${workerId}-${taskId}`);
    try {
      await api.post(`/onboarding/${workerId}/mark-complete`, {
        task_type: taskType,
        task_id: taskId,
        completed_by: 'owner'
      });
      toast.success('Task marked as complete');
      fetchOnboardingTracker();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to mark complete');
    } finally {
      setProcessingTask(null);
    }
  };

  // Print checklist for worker
  const handlePrintChecklist = (worker) => {
    const isDocComplete = (status) => ['completed', 'uploaded', 'approved'].includes(status);
    const printContent = `
      <html>
        <head>
          <title>Onboarding Checklist - ${worker.worker_name}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; }
            h1 { color: #D4AF37; border-bottom: 2px solid #D4AF37; padding-bottom: 10px; }
            h2 { color: #333; margin-top: 30px; }
            .info { background: #f5f5f5; padding: 15px; border-radius: 8px; margin-bottom: 20px; }
            .checklist { list-style: none; padding: 0; }
            .checklist li { padding: 10px 0; border-bottom: 1px solid #eee; display: flex; align-items: center; }
            .checkbox { width: 20px; height: 20px; border: 2px solid #D4AF37; border-radius: 4px; margin-right: 15px; display: inline-flex; align-items: center; justify-content: center; }
            .completed .checkbox { background: #D4AF37; color: white; }
            .completed { color: #22c55e; }
            .pending { color: #666; }
            .progress { background: #eee; height: 20px; border-radius: 10px; overflow: hidden; margin: 20px 0; }
            .progress-bar { background: #D4AF37; height: 100%; }
            @media print { body { padding: 20px; } }
          </style>
        </head>
        <body>
          <h1>NC Help Hub - Onboarding Checklist</h1>
          <div class="info">
            <strong>Employee:</strong> ${worker.worker_name}<br>
            <strong>Email:</strong> ${worker.worker_email}<br>
            <strong>Start Date:</strong> ${new Date(worker.started_at).toLocaleDateString()}<br>
            <strong>Deadline:</strong> ${new Date(worker.deadline).toLocaleDateString()}<br>
            <strong>Progress:</strong> ${worker.progress_percent}%
          </div>
          <div class="progress"><div class="progress-bar" style="width: ${worker.progress_percent}%"></div></div>
          
          <h2>Required Documents (${worker.documents_completed}/${worker.documents_total})</h2>
          <ul class="checklist">
            ${(worker.documents || []).map(doc => `
              <li class="${isDocComplete(doc.status) ? 'completed' : 'pending'}">
                <span class="checkbox">${isDocComplete(doc.status) ? '✓' : ''}</span>
                ${doc.name} ${isDocComplete(doc.status) ? '- Completed' : '- Pending'}
              </li>
            `).join('')}
          </ul>
          
          <h2>Required Waivers (${worker.waivers_completed}/${worker.waivers_total})</h2>
          <ul class="checklist">
            ${(worker.waivers || []).map(waiver => `
              <li class="${waiver.status === 'signed' ? 'completed' : 'pending'}">
                <span class="checkbox">${waiver.status === 'signed' ? '✓' : ''}</span>
                ${waiver.name} ${waiver.status === 'signed' ? '- Signed' : '- Pending Signature'}
              </li>
            `).join('')}
          </ul>
          
          <p style="margin-top: 40px; font-size: 12px; color: #999;">
            Printed on ${new Date().toLocaleString()} | NC Help Hub
          </p>
        </body>
      </html>
    `;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    printWindow.document.open();
    printWindow.document.write(DOMPurify.sanitize(printContent, { WHOLE_DOCUMENT: true }));
    printWindow.document.close();
    printWindow.print();
  };

  // Handle file upload for a document
  const handleUploadComplete = async (uploadResult, workerId, docType) => {
    try {
      await api.post(`/onboarding/${workerId}/upload-document`, {
        document_type: docType,
        file_url: uploadResult.file_url,
        file_name: uploadResult.filename
      });
      toast.success('Document uploaded successfully');
      setUploadingFor(null);
      fetchOnboardingTracker();
    } catch (e) {
      toast.error('Failed to save document');
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="onboarding-tab">
      {/* Sub-tabs */}
      <div className="flex items-center gap-2 mb-6 border-b border-[var(--border-primary)] pb-4">
        <Button
          variant={activeSubTab === 'tracker' ? 'default' : 'ghost'}
          size="sm"
          onClick={() => setActiveSubTab('tracker')}
          className={activeSubTab === 'tracker' ? 'bg-[var(--primary)] text-black' : ''}
          data-testid="onboarding-tracker-tab"
        >
          <Users className="w-4 h-4 mr-2" />
          Worker Tracker
        </Button>
        <Button
          variant={activeSubTab === 'settings' ? 'default' : 'ghost'}
          size="sm"
          onClick={() => setActiveSubTab('settings')}
          className={activeSubTab === 'settings' ? 'bg-[var(--primary)] text-black' : ''}
          data-testid="onboarding-settings-tab"
        >
          <Settings className="w-4 h-4 mr-2" />
          Forms Settings
        </Button>
      </div>

      {/* Settings View */}
      {activeSubTab === 'settings' && (
        <OnboardingFormsSettings />
      )}

      {/* Tracker View */}
      {activeSubTab === 'tracker' && (
        <>
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-2xl lg:text-3xl font-bold font-['Cormorant_Garamond']">Worker Onboarding Tracker</h1>
            <Button onClick={fetchOnboardingTracker} variant="outline" size="sm" data-testid="refresh-onboarding-btn">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
      
      {/* Status Cards */}
      {onboardingTracker && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <div className="card p-4 text-center">
            <div className="text-3xl font-bold text-[var(--primary)]">{onboardingTracker.total_workers}</div>
            <div className="text-sm text-[var(--text-muted)]">Total</div>
          </div>
          <div className="card p-4 text-center border-[var(--warning)]/30 bg-[var(--warning)]/10">
            <div className="text-3xl font-bold text-[var(--warning)]">{onboardingTracker.pending}</div>
            <div className="text-sm text-[var(--warning)]">Pending</div>
          </div>
          <div className="card p-4 text-center border-[#D4AF37]/30 bg-[#D4AF37]/10">
            <div className="text-3xl font-bold text-[#D4AF37]">{onboardingTracker.in_progress}</div>
            <div className="text-sm text-[#D4AF37]">In Progress</div>
          </div>
          <div className="card p-4 text-center border-[var(--success)]/30 bg-[var(--success)]/10">
            <div className="text-3xl font-bold text-[var(--success)]">{onboardingTracker.approved}</div>
            <div className="text-sm text-[var(--success)]">Approved</div>
          </div>
          <div className="card p-4 text-center border-[var(--error)]/30 bg-[var(--error)]/10">
            <div className="text-3xl font-bold text-[var(--error)]">{onboardingTracker.blocked}</div>
            <div className="text-sm text-[var(--error)]">Blocked</div>
          </div>
        </div>
      )}

      {/* Workers List */}
      <div className="space-y-4">
        {onboardingTracker?.workers?.length === 0 ? (
          <div className="card p-8 text-center">
            <FileCheck className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-[var(--text-muted)]">No workers in onboarding</p>
          </div>
        ) : (
          onboardingTracker?.workers?.map((worker) => (
            <div 
              key={worker.worker_id}
              className={`card p-4 ${
                worker.onboarding_status === 'blocked' ? 'border-red-300 bg-[var(--error)]/10' :
                worker.onboarding_status === 'approved' ? 'border-green-300 bg-[var(--success)]/10' :
                worker.days_remaining <= 3 ? 'border-yellow-300 bg-[var(--warning)]/10' : ''
              }`}
              data-testid={`onboarding-worker-card-${worker.worker_id}`}
            >
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      worker.onboarding_status === 'blocked' ? 'bg-red-500' :
                      worker.onboarding_status === 'approved' ? 'bg-green-500' :
                      'bg-[var(--primary)]'
                    } text-white font-bold`}>
                      {worker.worker_name?.charAt(0) || '?'}
                    </div>
                    <div>
                      <h3 className="font-semibold">{worker.worker_name}</h3>
                      <p className="text-sm text-[var(--text-muted)]">{worker.worker_email}</p>
                    </div>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="mt-3">
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span>Progress: {worker.progress_percent}%</span>
                      <span className={`font-medium ${
                        worker.days_remaining <= 0 ? 'text-[var(--error)]' :
                        worker.days_remaining <= 3 ? 'text-[var(--warning)]' : 'text-[var(--success)]'
                      }`}>
                        {worker.days_remaining <= 0 ? 'OVERDUE' : `${worker.days_remaining} days left`}
                      </span>
                    </div>
                    <div className="h-2 bg-[var(--background-subtle)] rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full ${
                          worker.onboarding_status === 'blocked' ? 'bg-red-500' :
                          worker.onboarding_status === 'approved' ? 'bg-green-500' :
                          'bg-[var(--primary)]'
                        }`}
                        style={{ width: `${worker.progress_percent}%` }}
                      />
                    </div>
                  </div>

                  {/* Checklist Summary */}
                  <div className="flex gap-4 mt-3 text-sm">
                    <span className={worker.documents_completed === worker.documents_total ? 'text-[var(--success)]' : 'text-[var(--text-muted)]'}>
                      {worker.documents_completed === worker.documents_total ? <CheckCircle className="w-4 h-4 inline mr-1" /> : <Clock className="w-4 h-4 inline mr-1" />}
                      Docs: {worker.documents_completed}/{worker.documents_total}
                    </span>
                    <span className={worker.waivers_completed === worker.waivers_total ? 'text-[var(--success)]' : 'text-[var(--text-muted)]'}>
                      {worker.waivers_completed === worker.waivers_total ? <CheckCircle className="w-4 h-4 inline mr-1" /> : <Clock className="w-4 h-4 inline mr-1" />}
                      Waivers: {worker.waivers_completed}/{worker.waivers_total}
                    </span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => {
                      setSelectedOnboardingWorker(worker);
                      setShowOnboardingModal(true);
                    }}
                    data-testid={`view-onboarding-details-${worker.worker_id}`}
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    View Details
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={async () => {
                      try {
                        await onboardingAPI.sendReminder(worker.worker_id);
                        toast.success('Reminder sent');
                      } catch (e) {
                        toast.error('Failed to send reminder');
                      }
                    }}
                    data-testid={`send-reminder-${worker.worker_id}`}
                  >
                    <Mail className="w-4 h-4 mr-1" />
                    Send Reminder
                  </Button>
                  {worker.days_remaining <= 3 && worker.onboarding_status !== 'approved' && (
                    extendingWorker === worker.worker_id ? (
                      <div className="flex items-center gap-2">
                        <select 
                          value={extendDays} 
                          onChange={(e) => setExtendDays(parseInt(e.target.value))}
                          className="border border-[var(--border-primary)] rounded px-2 py-1 text-sm bg-[var(--background-subtle)] text-[var(--text-primary)]"
                        >
                          <option value={3}>+3 days</option>
                          <option value={7}>+7 days</option>
                          <option value={14}>+14 days</option>
                          <option value={30}>+30 days</option>
                        </select>
                        <Button size="sm" onClick={() => handleExtendDeadline(worker.worker_id)}>
                          Extend
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setExtendingWorker(null)}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="text-[#D4AF37] border-blue-600"
                        onClick={() => setExtendingWorker(worker.worker_id)}
                        data-testid={`extend-deadline-${worker.worker_id}`}
                      >
                        <Calendar className="w-4 h-4 mr-1" />
                        Extend
                      </Button>
                    )
                  )}
                  {worker.onboarding_status !== 'approved' && worker.progress_percent === 100 && (
                    <Button 
                      size="sm"
                      className="bg-green-500 hover:bg-[var(--success)] text-white"
                      onClick={async () => {
                        try {
                          await onboardingAPI.approveWorker(worker.worker_id, { can_work: true });
                          toast.success('Worker approved');
                          fetchOnboardingTracker();
                        } catch (e) {
                          toast.error('Failed to approve');
                        }
                      }}
                      data-testid={`approve-worker-${worker.worker_id}`}
                    >
                      <Check className="w-4 h-4 mr-1" />
                      Approve
                    </Button>
                  )}
                  {worker.can_work && (
                    <Button 
                      size="sm"
                      variant="destructive"
                      onClick={async () => {
                        if (!window.confirm('Block this worker from working?')) return;
                        try {
                          await onboardingAPI.approveWorker(worker.worker_id, { can_work: false, notes: 'Manually blocked by owner' });
                          toast.success('Worker blocked');
                          fetchOnboardingTracker();
                        } catch (e) {
                          toast.error('Failed to block');
                        }
                      }}
                      data-testid={`block-worker-${worker.worker_id}`}
                    >
                      <X className="w-4 h-4 mr-1" />
                      Block
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handlePrintChecklist(worker)}
                    title="Print checklist"
                    data-testid={`print-checklist-${worker.worker_id}`}
                  >
                    <Printer className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setExpandedWorker(expandedWorker === worker.worker_id ? null : worker.worker_id)}
                    data-testid={`expand-checklist-${worker.worker_id}`}
                  >
                    {expandedWorker === worker.worker_id ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>

              {/* Expanded Checklist View */}
              <AnimatePresence>
                {expandedWorker === worker.worker_id && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="mt-4 pt-4 border-t border-[var(--border-primary)]">
                      <div className="grid md:grid-cols-2 gap-6">
                        {/* Documents Checklist */}
                        <div>
                          <h4 className="font-semibold mb-3 flex items-center gap-2">
                            <FileText className="w-4 h-4 text-[var(--primary)]" />
                            Required Documents ({worker.documents_completed}/{worker.documents_total})
                          </h4>
                          <div className="space-y-2">
                            {(worker.documents || []).map((doc, idx) => {
                              const isComplete = ['completed', 'uploaded', 'approved'].includes(doc.status);
                              return (
                              <div 
                                key={doc.id || idx}
                                className={`flex items-center justify-between p-3 rounded-lg ${
                                  isComplete 
                                    ? 'bg-[var(--success)]/10' 
                                    : 'bg-[var(--background-subtle)]'
                                }`}
                              >
                                <div className="flex items-center gap-2">
                                  {isComplete ? (
                                    <CheckCircle className="w-5 h-5 text-[var(--success)]" />
                                  ) : (
                                    <Clock className="w-5 h-5 text-[var(--text-muted)]" />
                                  )}
                                  <span className={isComplete ? 'text-[var(--success)]' : ''}>
                                    {doc.name}
                                  </span>
                                </div>
                                <div className="flex gap-1">
                                  {!isComplete && (
                                    <>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => setUploadingFor({ workerId: worker.worker_id, docType: doc.type, docName: doc.name })}
                                        title="Upload document"
                                        className="text-[var(--primary)]"
                                      >
                                        <Upload className="w-4 h-4" />
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => handleMarkComplete(worker.worker_id, 'document', doc.type)}
                                        disabled={processingTask === `${worker.worker_id}-${doc.type}`}
                                        title="Mark complete"
                                        className="text-[var(--success)]"
                                      >
                                        <Check className="w-4 h-4" />
                                      </Button>
                                    </>
                                  )}
                                  {doc.file_url && (
                                    <a
                                      href={doc.file_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="p-1 text-[var(--primary)] hover:bg-[var(--primary)]/10 rounded"
                                      title="View document"
                                    >
                                      <Eye className="w-4 h-4" />
                                    </a>
                                  )}
                                </div>
                              </div>
                            )})}
                            {(!worker.documents || worker.documents.length === 0) && (
                              <p className="text-sm text-[var(--text-muted)] italic">No documents configured</p>
                            )}
                          </div>
                        </div>

                        {/* Waivers Checklist */}
                        <div>
                          <h4 className="font-semibold mb-3 flex items-center gap-2">
                            <FileCheck className="w-4 h-4 text-purple-500" />
                            Required Waivers ({worker.waivers_completed}/{worker.waivers_total})
                          </h4>
                          <div className="space-y-2">
                            {(worker.waivers || []).map((waiver, idx) => (
                              <div 
                                key={waiver.id || idx}
                                className={`flex items-center justify-between p-3 rounded-lg ${
                                  waiver.status === 'signed' 
                                    ? 'bg-[var(--success)]/10' 
                                    : 'bg-[var(--background-subtle)]'
                                }`}
                              >
                                <div className="flex items-center gap-2">
                                  {waiver.status === 'signed' ? (
                                    <CheckCircle className="w-5 h-5 text-[var(--success)]" />
                                  ) : (
                                    <Clock className="w-5 h-5 text-[var(--text-muted)]" />
                                  )}
                                  <span className={waiver.status === 'signed' ? 'text-[var(--success)]' : ''}>
                                    {waiver.name}
                                  </span>
                                </div>
                                <div className="flex gap-1">
                                  {waiver.status !== 'signed' && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleMarkComplete(worker.worker_id, 'waiver', waiver.id)}
                                      disabled={processingTask === `${worker.worker_id}-${waiver.id}`}
                                      title="Mark as signed"
                                      className="text-[var(--success)]"
                                    >
                                      <Check className="w-4 h-4" />
                                    </Button>
                                  )}
                                  {waiver.signed_at && (
                                    <span className="text-xs text-[var(--text-muted)]">
                                      {new Date(waiver.signed_at).toLocaleDateString()}
                                    </span>
                                  )}
                                </div>
                              </div>
                            ))}
                            {(!worker.waivers || worker.waivers.length === 0) && (
                              <p className="text-sm text-[var(--text-muted)] italic">No waivers configured</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))
        )}
      </div>

      {/* Upload Modal */}
      <AnimatePresence>
        {uploadingFor && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setUploadingFor(null)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              className="bg-[var(--background-paper)] rounded-xl max-w-md w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Upload Document</h3>
                <button onClick={() => setUploadingFor(null)} className="p-1 hover:bg-gray-100 rounded">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-sm text-[var(--text-muted)] mb-4">
                Upload <strong>{uploadingFor.docName}</strong> for this worker
              </p>
              <UniversalUploader
                category="document"
                destination={`onboarding/${uploadingFor.workerId}`}
                label={uploadingFor.docName}
                onUploadComplete={(result) => handleUploadComplete(result, uploadingFor.workerId, uploadingFor.docType)}
                acceptedTypes={['image/*', 'application/pdf']}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      </>
      )}
    </motion.div>
  );
};

export default OnboardingTab;
